import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { DynamicFormComponent } from '../../components/dynamic-form/dynamic-form.component';

@Component({
  selector: 'app-appointment-info-dialog',
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatListModule, MatIconModule, DynamicFormComponent],
  template: `
    <div *ngIf="!data.appointment.isGrouped; else groupedTemplate">
      <h2 mat-dialog-title>Schedule Entry</h2>
      <mat-dialog-content>
        <app-dynamic-form 
          [model]="'ScheduleEntry'" 
          [itemId]="data.appointment.id"
          [hideRelationships]="true"
          [hideSaveButton]="false">
        </app-dynamic-form>
      </mat-dialog-content>
      <mat-dialog-actions align="end">
        <button mat-button (click)="onClose()">Close</button>
      </mat-dialog-actions>
    </div>

    <ng-template #groupedTemplate>
      <h2 mat-dialog-title>Grouped Appointments ({{ data.appointment.count }})</h2>
      <mat-dialog-content>
        <mat-action-list>
          <mat-list-item *ngFor="let appointment of data.appointment.appointments" (click)="onAppointmentClick(appointment)">
            <mat-icon matListItemIcon>event</mat-icon>
            <div matListItemTitle>{{ appointment.name }}</div>
            <div matListItemLine>{{ formatAppointmentDateTime(appointment) }}</div>
          </mat-list-item>
        </mat-action-list>
      </mat-dialog-content>
      <mat-dialog-actions align="end">
        <button mat-button (click)="onClose()">Close</button>
      </mat-dialog-actions>
    </ng-template>
  `,
  styles: [`
    mat-dialog-content {
      padding: 20px;
      min-width: 500px;
      max-height: 70vh;
      overflow-y: auto;
    }
    
    mat-action-list {
      padding: 0;
    }
    
    mat-list-item {
      cursor: pointer;
      border-bottom: 1px solid #e0e0e0;
    }
    
    mat-list-item:hover {
      background-color: #f5f5f5;
    }
    
    mat-list-item:last-child {
      border-bottom: none;
    }
  `]
})
export class AppointmentInfoDialogComponent {
  readonly dialogRef = inject(MatDialogRef<AppointmentInfoDialogComponent>);
  readonly data = inject(MAT_DIALOG_DATA);
  readonly dialog = inject(MatDialog);

  getGroupedEntryMessage(): string {
    const count = this.data.appointment.count || 0;
    const period = this.data.selectedPeriod?.label || 'period';
    return `You clicked on a grouped entry containing ${count} appointment${count !== 1 ? 's' : ''} in the ${period} view.`;
  }

  formatAppointmentDateTime(appointment: any): string {
    if (!appointment.start_date_time) return 'No date/time';
    
    const startDate = new Date(appointment.start_date_time);
    const endDate = new Date(appointment.end_date_time);
    
    const dateStr = startDate.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
    
    const startTimeStr = startDate.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    
    const endTimeStr = endDate.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    
    return `${dateStr}, ${startTimeStr} - ${endTimeStr}`;
  }

  onAppointmentClick(appointment: any): void {
    // Close the current dialog and open a new one with the individual appointment
    this.dialogRef.close();
    
    // Re-open dialog with individual appointment data
    const dialogRef = this.dialog.open(AppointmentInfoDialogComponent, {
      data: { 
        appointment: appointment,
        selectedPeriod: this.data.selectedPeriod,
        schedule: this.data.schedule
      },
    });
  }

  onClose(): void {
    this.dialogRef.close();
  }
}